import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, CircularProgress, styled} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import {FileDownload, UploadFile} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import {
    downloadFormDocument,
    useDeleteFormDocumentMutation,
    useUploadFormDocumentsMutation
} from '../../../../../api/services/filesService';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import Uploader from '../../../../SchoolManagement/Setup/Uploader';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const DownloadIcon = styled(FileDownload)(({theme}) => ({
    color: theme.palette.primary.main,
    cursor: 'pointer'
}));

const DeleteAttachmentIcon = styled(DeleteIcon)(({theme}) => ({
    color: theme.palette.primary.main
}));

const Attachment = ({
                        componentId,
                        index,
                        onChange,
                        descriptionHtml,
                        onDescriptionHtmlChange,
                        commitDescriptionHtml,
                        isEditMode,
                        attachments,
                        isFormArchived,
                        readOnly,
                        placeholderValues,
                        accessLevel,
                        audience,
                        recipientType,
                        submitMode
                    }) => {
    const [error, setError] = useState('');
    const [uploadFiles] = useUploadFormDocumentsMutation();
    const [deleteFile] = useDeleteFormDocumentMutation();
    const [openAddDocumentsDialog, setOpenAddDocumentsDialog] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadSizeExceedsLimit, setIsUploadSizeExceedsLimit] = useState(false);
    const downloadFile = useFileDownloader();

    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFilesUpload = async () => {
        setIsUploading(true);
        const uploadedFiles = await uploadFiles(selectedFiles);
        onChange({index, files: [...attachments, ...uploadedFiles.data]});
        handleDialogClose();
    };

    const deleteAttachment = async (fileId) => {
        try {
            await deleteFile({documentId: fileId});
            onChange({index, files: attachments.filter(file => file.id !== fileId)});
            setError('');
        } catch (error) {
            setError('Something went wrong. Please try again later');
        }
    };

    const handleDialogClose = () => {
        setOpenAddDocumentsDialog(false);
        setSelectedFiles([]);
        setIsUploading(false);
        setIsUploadSizeExceedsLimit(false);
    };

    const handleFilesSelect = (files) => {
        const newFiles = files.filter(
            file =>
                !selectedFiles.some(s => s.name === file.name) &&
                !attachments.some(s => s.name === file.name)
        );
        setSelectedFiles([...selectedFiles, ...newFiles]);
    };

    const handleFileDeleteFromUploadContext = (filename) => {
        const files = [...selectedFiles];
        setSelectedFiles(files.filter(f => f.name !== filename));
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFile/>}
                            onClick={() => setOpenAddDocumentsDialog(true)}
                            disabled={isFormArchived}
                        >
                            Choose Files
                        </Button>

                        {error && (
                            <Typography key={`error-${error}`} color="error">
                                {error}
                            </Typography>
                        )}

                        {attachments.map(file => (
                            <Box
                                py={1}
                                display="flex"
                                justifyContent="space-between"
                                key={`file-${file?.id ?? file.name}`}
                            >
                                <Typography>{file.name}</Typography>
                                <DeleteAttachmentIcon
                                    cursor={isFormArchived ? '' : 'pointer'}
                                    onClick={() =>
                                        isFormArchived ? {} : deleteAttachment(file.id)
                                    }
                                />
                        </Box>
                        ))}

                        <RichEditor
                            onChange={onChange}
                            index={index}
                            isEditMode={isEditMode}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            audience={audience}
                            recipientType={recipientType}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />

                    <AdmicityDialog
                        handleClose={handleDialogClose}
                        title="Add documents"
                        open={openAddDocumentsDialog}
                        actions={[
                            {label: 'Cancel', onClick: handleDialogClose},
                            {
                                label: 'Upload',
                                onClick: handleFilesUpload,
                                disabled:
                                    (isUploading || selectedFiles.size === 0) ||
                                    isUploadSizeExceedsLimit,
                                startIcon: isUploading
                                    ? (
                                        <CircularProgress color="inherit" size={16}/>
                                    )
                                    : undefined
                            }
                        ]}
                        maxWidth="md"
                    >
                        <Uploader
                            totalUploadSizeInMb={25}
                            onFilesSelect={handleFilesSelect}
                            onFileDelete={handleFileDeleteFromUploadContext}
                            onUploadLimitExceed={setIsUploadSizeExceedsLimit}
                            value={selectedFiles}
                            readonly={isUploading}
                        />
                    </AdmicityDialog>
                </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={false}
                            audience={audience}
                            recipientType={recipientType}
                            submitMode={submitMode}
                        />
                        {attachments?.map(file => (
                            <Box
                                py={1}
                                display="flex"
                                justifyContent="space-between"
                                key={`file-${file?.id ?? file.name}`}
                            >
                                <Typography fontWeight="bold">{file.name}</Typography>
                            <DownloadIcon
                                onClick={async () =>
                                    await downloadFile(downloadFormDocument, {
                                        documentId: file.id
                                    })
                                }
                            />
                        </Box>
                        ))}
                </>
                )}
        </Box>
    );
};

Attachment.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    index: PropTypes.number,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    attachments: PropTypes.array,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    submitMode: PropTypes.bool
};

export default Attachment;
